import { Box, Button, Divider, Grid, ListItem, ListItemText, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { PricerLayout } from '../../internalPricer/components/internalPricerComponents';
import { PropertyState, LoanSubType, SubmissionDate, FloorRate, BorrowerRate, BorrowerPoints, RocRate, RocPoints, YieldSpread, YourPoints, createCustomSelectField, LoanTerm, LoanPurpose, PropertyType, PurchaseDate } from './clientBridgePricerFields';
import { ClientBridgePricerStore } from '../stores/clientBridgePricerStore';
import BridgePricerChart from '../utils/bridgePricerChart';

import { useEffect, useMemo } from 'react';
import { LoaSubtypeOptions, NON_BREAKING_SPACE, borrowerExperienceData, borrowerExperienceHeaders, borrowerRateOptions, loanTermOptions, propertyTypeOptionsFnFGU, tooltipDataBorrowerExperience } from '../utils/bridgePricerConstants';
import { AutocompleteField } from '../../dscrCalculator/components/autocompleteField';
import { debounce, estimatedFFExperienceOptions, estimatedGUExperienceOptions, formatDate, loanPurpose, sanitizeTestId, yesNoOptionsBoolean } from '@roc/client-portal-shared/utils';
import { createAutocompleteAddressField, createCurrencyField, createDateField, createRadioField, createSelectField, createSliderField, Paper, PaperBorderType, TestDataAttribute } from '@roc/ui';
import TooltipWithTable from './tooltipWithTable';
import { formatPercentage } from '@roc/ui/utils';

const useStyles = makeStyles(theme => ({
  slider: {
    marginTop: '2.5em',
    marginLeft: '2em',
    width: '85%',
  },
  paper: {
    border: '1px solid',
    borderColor: theme.palette.grey['200'],
    width: '100%',
    padding: theme.spacing(1),
  },
  divider: {
    listStyle: 'none',
  },
  span: {
    color: '#d32f2f',
    fontSize: '0.8rem'
  }
}));

const preConfiguredBorrowerExperienceTooltip = () => {
  return <TooltipWithTable tableData={borrowerExperienceData} tableHeaders={borrowerExperienceHeaders} />
};

const formatPercentageValue = value => {
  if (value === null || value === undefined) {
    return '-';
  }
  return formatPercentage(value, 2);
}

export const PaperList = ({ children }) => {
  const classes = useStyles();
  return <Paper borderType={PaperBorderType.SOLID_BORDER} className={classes.paper}>{children}</Paper>;
};

export const PaperListTitle = ({ text, last = false }) => {
  const classes = useStyles();
  return (
    <ListItem>
      <ListItemText>
        <Typography variant="h5">{text}</Typography>
      </ListItemText>
    </ListItem>
  );
};

export const PaperListItem = ({ label, value, last = false }) => {
  const classes = useStyles();
  return (
    <>
      <Divider variant="middle" component="li" className={classes.divider} />
      <ListItem>
        <ListItemText>
          <Box display="flex">
            <Box flexGrow={1}>{label}</Box>
            <TestDataAttribute id={sanitizeTestId(label)}>
              <Typography variant="body1">{value}</Typography>
            </TestDataAttribute>
          </Box>
        </ListItemText>
      </ListItem>
    </>
  );
};

interface PricerCalcSectionProps {
  store: ClientBridgePricerStore;
  getNewFloorRate?: () => void;
}

export const ClientBridgePricer = observer(({ store }: PricerCalcSectionProps) => {
  const formValues = store.getFormValues();
  const minFico = formValues.estimatedFicoScore;
  useEffect(() => {
    const actualDate = new Date();
    store.onFieldChange('submissionDate', actualDate);
    store.onFieldChange('lenderId', store.lenderId);
    onChangedDate(store, actualDate);
  }, []);

  const classes = useStyles();

  const onChangedDate = async (store, actualDate = null) => {
    try {
      const response = await store.getRateFloor(
        {
          "submissionDate": actualDate || store.form.fields["submissionDate"].value,
          "lenderName": store.form.fields['lenderName'].value?.label || '',
          "propertyState": store.form.fields["propertyState"].value,
          "loanSubtype": store.form.fields["loanSubtype"].value
        });
      store.onFieldChange("floorRate", response.data);
      store.filterBorowerRateOptions(borrowerRateOptions, response.data);
      store.getBorrowerPointsOptions(
        store.form.fields["lenderId"].value,
        store.form.fields["loanTermsMonths"].value,
        formatDate(store.form.fields["submissionDate"].value, 'MM/dd/yyyy')
      );
    } catch (err) {

    }
  }

  const fetchOptions = useMemo(
    () => debounce(searchText => store.fetchOptions(searchText), 500),
    [store]
  );

  return (
    <PricerLayout
      left={
        <>
          <Grid item xs={12}>
            <AutocompleteField
              value={store.loanSearchText}
              options={store.loanSearchOptions}
              onChange={text => {
                store.loanSearchText = text;
                fetchOptions(store.loanSearchText);
              }}
              onSelect={opt => {
                store.loanSearchText = opt.label;
                store.fetchLoanValues(opt.value);
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <PropertyDetailsSection store={store} getNewFloorRate={() => { onChangedDate(store) }} />
          </Grid>
          <Grid item xs={12} style={{ display: store.form.fields.loanSubtype.value === LoaSubtypeOptions.GROUNDUP ? '' : 'none' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant={'h5'}>Borrower Details</Typography>
              </Grid>
              <Grid item xs={6}>
                {createSliderField({
                  store,
                  label: "Borrower's FICO Score",
                  fieldName: 'estimatedFicoScore',
                  testId: 'estimatedFicoScore',
                  className: classes.slider,
                  valueLabelDisplay: 'on',
                  min: 680 > minFico ? minFico : 680,
                  max: 800,
                  step: 10,
                  marks: [
                    {
                      value: 680,
                      label: '680',
                    },
                    {
                      value: 800,
                      label: '800',
                    },
                  ],
                  onBlur: () => {
                    onChangedDate(store);
                  }
                })}
                {(680 > minFico) && (
                  <span className={classes.span}>
                    The borrower's fico score must be at least 680.
                  </span>)}
              </Grid>
              <Grid item xs={6}>
                {createCustomSelectField({
                  store,
                  label: `Borrower's Experience Score`,
                  fieldName: 'experienceScore',
                  testId: 'experienceScore',
                  options: estimatedGUExperienceOptions,
                })}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} style={{ display: store.form.fields.loanSubtype.value === LoaSubtypeOptions.FIX_AND_FLIP_1_4 ? '' : 'none' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant={'h5'}>Borrower Details</Typography>
              </Grid>
              <Grid item xs={6}>
                {createSliderField({
                  store,
                  label: "Borrower's FICO Score",
                  fieldName: 'estimatedFicoScore',
                  testId: 'estimatedFicoScore',
                  className: classes.slider,
                  valueLabelDisplay: 'on',
                  min: 500 > minFico ? minFico : 500,
                  max: 800,
                  step: 10,
                  marks: [
                    {
                      value: 500,
                      label: '500',
                    },
                    {
                      value: 800,
                      label: '800',
                    },
                  ],
                  onBlur: () => {
                    onChangedDate(store);
                  }
                })}
                {(500 > minFico) && (
                  <span style={{ color: '#d32f2f', fontSize: '0.8rem' }}>
                    The borrower's fico score must be at least 500.
                  </span>)}
              </Grid>
              <Grid item xs={6}>
                {createCustomSelectField({
                  store,
                  label: `Borrower's Experience Score`,
                  fieldName: 'experienceScore',
                  testId: 'experienceScore',
                  options: estimatedFFExperienceOptions,
                })}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <RatesSection store={store} />
          </Grid>
          <Grid item xs={12}>
            <Button color='primary' variant='contained' size='large' fullWidth onClick={() => store.fetchValues()}>
              CALCULATE
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Notes store={store} />
          </Grid>
        </>
      }
      right={
        <>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <PaperList>
                <Box p={1}>
                  <Chart key={store.chartData['yourYieldSpread']} store={store} />
                </Box>
              </PaperList>
            </Grid>
            <Grid item xs={12}>
              <PaperList>
                <PaperListTitle text="Yield Spread Values" />
                <PaperListItem label="Capital Provider Rate" value={formatPercentageValue(formValues.rocRate)} />
                <PaperListItem label="Capital Provider Points" value={formatPercentageValue(formValues.rocPoints)} />
                <PaperListItem label="Your Yield Spread" value={formatPercentageValue(formValues.yourYieldSpread)} />
                <PaperListItem label="Your Points" value={formatPercentageValue(formValues.yourPoints)} />
              </PaperList>
            </Grid>
          </Grid>
        </>
      }
    />
  )
});

export const PropertyDetailsSection =
  observer(({ store, getNewFloorRate }: PricerCalcSectionProps) => {
    const classes = useStyles();

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant={'h5'}>Property Details</Typography>
        </Grid>
        <LoanSubType store={store} />
        {(store.form.fields.loanSubtype.value !== LoaSubtypeOptions.FIX_AND_FLIP_1_4) && (
          <PropertyState store={store} />
        )}
        {(store.form.fields.loanSubtype.value === LoaSubtypeOptions.FIX_AND_FLIP_1_4) && (<>
          <Grid item xs={6}>
            {createAutocompleteAddressField({
              store,
              label: 'Property Address',
              fieldName: 'propertyAddress',
              testId: 'propertyAddress',
              onChange: getNewFloorRate,
            })}
          </Grid>
        </>)}
        <LoanTerm store={store} />
        {(store.form.fields.loanSubtype.value === LoaSubtypeOptions.GROUNDUP || store.form.fields.loanSubtype.value === LoaSubtypeOptions.FIX_AND_FLIP_1_4) && (<>
          <LoanPurpose store={store} />
        </>)}
        <Grid item xs={12} style={{ display: store.form.fields.loanSubtype.value === LoaSubtypeOptions.GROUNDUP ? '' : 'none' }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              {createCurrencyField({
                store,
                label: 'Total Renovation Budget',
                fieldName: 'renoBudget',
                testId: 'renoBudget',
                onBlur: getNewFloorRate,
              })}
            </Grid>
            <Grid item xs={6}>
              {createSliderField({
                store,
                label: "Loan to Final Cost",
                fieldName: 'loanToPurchase',
                testId: 'loanToPurchase',
                className: classes.slider,
                valueLabelDisplay: 'on',
                valueLabelFormat: (value) => (<div>{Math.round(value)}%</div>),
                min: 0,
                max: 90,
                step: 5,
                marks: [
                  {
                    value: 0,
                    label: '0%',
                  },
                  {
                    value: 90,
                    label: '90%',
                  },
                ],
                onBlur: getNewFloorRate,
              })}
            </Grid>
            {store.form.fields.loanPurpose.value === 'Refinance' &&
              (<>
                <Grid item xs={6}>
                  {createCurrencyField({
                    store,
                    label: 'Completed Renovations',
                    fieldName: 'completedCapitalImprovements',
                    testId: 'completedCapitalImprovements',
                    onBlur: getNewFloorRate,
                  })}
                </Grid>
              </>)
            }
          </Grid>
        </Grid>
        <Grid item xs={12} style={{ display: store.form.fields.loanSubtype.value === LoaSubtypeOptions.FIX_AND_FLIP_1_4 ? '' : 'none' }}>
          <Grid container spacing={2}>
            {store.form.fields.loanPurpose.value === 'Refinance' &&
              (<>
                <PurchaseDate store={store} />
                <Grid item xs={6}>
                  {createCurrencyField({
                    store,
                    label: 'Payoff Amount',
                    fieldName: 'payoffAmount',
                    testId: 'payoffAmount',
                    onBlur: getNewFloorRate,
                  })}
                </Grid>
                <Grid item xs={6}>
                  {createCurrencyField({
                    store,
                    label: 'Construction/Reno Spent to Date',
                    fieldName: 'completedCapitalImprovements',
                    testId: 'completedCapitalImprovements',
                    onBlur: getNewFloorRate,
                  })}
                </Grid>
              </>)
            }
            <PropertyType store={store} />
            <Grid item xs={6}>
              {createCurrencyField({
                store,
                label: 'Renovation Budget',
                fieldName: 'renoBudget',
                testId: 'renoBudget',
                onBlur: getNewFloorRate,
              })}
            </Grid>
            <Grid item xs={6}>
              {createSliderField({
                store,
                label: "Initial Loan to Cost",
                fieldName: 'loanToPurchase',
                testId: 'loanToPurchase',
                className: classes.slider,
                valueLabelDisplay: 'on',
                valueLabelFormat: (value) => (<div>{Math.round(value)}%</div>),
                min: 0,
                max: 90,
                step: 5,
                marks: [
                  {
                    value: 0,
                    label: '0%',
                  },
                  {
                    value: 90,
                    label: '90%',
                  },
                ],
                onBlur: getNewFloorRate,
              })}
            </Grid>
            <Grid item xs={6}>
              {createCurrencyField({
                store,
                label: 'Total Loan Amount',
                fieldName: 'loanAmount',
                testId: 'loanAmount',
                onBlur: getNewFloorRate,
              })}
            </Grid>
            <Grid item xs={6}>
              {createCurrencyField({
                store,
                label: 'ARV',
                fieldName: 'arv',
                testId: 'arv',
                onBlur: getNewFloorRate,
              })}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  });

export const RatesSection =
  ({ store }: PricerCalcSectionProps) => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant={'h5'}>Fix & Flip and Ground Up Rates</Typography>
        </Grid>
        <SubmissionDate store={store} />
        <FloorRate store={store} />
        <BorrowerRate store={store} />
        <BorrowerPoints store={store} />
      </Grid>
    );
  }

const Chart = observer(({
  store,
}: PricerCalcSectionProps) => {
  const { chartData } = store;
  return (
    <>
      <Grid item xs={12}>
        <Typography variant={'h5'}>Your Yield Spread vs. Borrower Rate</Typography>
        <BridgePricerChart key={chartData['yourYieldSpread']} chartData={chartData} />
      </Grid>
    </>
  );
})

export const Notes = observer(
  ({ store }: PricerCalcSectionProps) => {
    return store.chartData.length > 1 ? (
      <>
        <Typography variant={'subtitle2'}>
          Notes:
        </Typography>
        <Typography variant={'caption'}>
          While the rate floor is set at {store.form.fields['floorRate'].value}, a yield spread is available at rates above the floor rate and the point share to the Capital Provider is increased for rates below 10.99. All rates quoted are valid for 30 days from the date of submission.
        </Typography>
      </>
    ) : null;
  }
)